import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './lib/supabase';

const Ctx = createContext<any>(null);
export const useAuth = () => useContext(Ctx);

export function AuthProvider({children}:{children:JSX.Element}) {
  const [user,setUser]=useState<any>(null); 
  const [session,setSession]=useState<any>(null); 
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    console.log('🔐 AuthProvider: Initializing...')
    console.log('🔐 AuthProvider: Environment check:', {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? 'set' : 'missing',
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'set' : 'missing',
      supabaseClient: !!supabase ? 'initialized' : 'missing'
    })
    
    supabase.auth.getSession().then(({data, error})=>{
      console.log('🔐 AuthProvider: getSession result:', {
        session: data.session ? 'exists' : 'null',
        user: data.session?.user ? 'exists' : 'null', 
        access_token: data.session?.access_token ? 'exists' : 'null',
        error: error
      })
      
      // If there's an error getting the session, clear everything
      if (error) {
        console.error('🔐 AuthProvider: Session error:', error)
        clearSession()
        return
      }
      
      setUser(data.session?.user||null); 
      setSession(data.session||null);
      setLoading(false);
      
      console.log('🔐 AuthProvider: State updated - User:', data.session?.user ? 'logged in' : 'not logged in')
    });
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s)=>{
      console.log('🔐 AuthProvider: Auth state changed:', {
        event,
        session: s ? 'exists' : 'null',
        user: s?.user ? 'exists' : 'null',
        access_token: s?.access_token ? 'exists' : 'null'
      })
      
      // Handle sign out events or session errors
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        if (event === 'TOKEN_REFRESHED' && !s) {
          console.log('🔐 AuthProvider: Token refresh failed, clearing session')
          clearSession()
          return
        }
      }
      
      setUser(s?.user||null);
      setSession(s||null);
    });

    return () => subscription.unsubscribe();
  },[]);

  const magic = (email:string) => {
    console.log('🔐 AuthProvider: Sending magic link to:', email)
    
    // If a redirect param is present in URL, send magic link back there; otherwise use /auth/confirm
    const sp = new URLSearchParams(window.location.search)
    const redirectParam = sp.get('redirect')
    const redirectUrl = redirectParam 
      ? `${window.location.origin}${redirectParam}`
      : `${window.location.origin}/auth/confirm`
    
    console.log('🔐 AuthProvider: Using redirect URL:', redirectUrl)
    
    return supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: redirectUrl
      }
    });
  }
  
  const logout = async () => {
    console.log('🔐 AuthProvider: Logging out...')
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('🔐 AuthProvider: Logout error:', error)
        // Force clear session even if logout fails
        if (error.status === 403 || error.message?.includes('403')) {
          console.log('🔐 AuthProvider: 403 error, force clearing session')
          clearSession()
        }
      }
    } catch (e) {
      console.error('🔐 AuthProvider: Logout exception:', e)
      // Force clear session on any error
      clearSession()
    }
  }

  const clearSession = async () => {
    console.log('🔐 AuthProvider: Clearing session...')
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
    // Clear local storage
    localStorage.clear()
    sessionStorage.clear()
  }

  console.log('🔐 AuthProvider: Render - Current state:', {
    user: user ? 'logged in' : 'not logged in',
    session: session ? 'exists' : 'null',
    loading
  })

  return <Ctx.Provider value={{user,session,magic,logout,clearSession,loading}}>{children}</Ctx.Provider>;
} 
